import { DateTime } from 'luxon';
import { getShiftCodeAt } from '../../../../shared/services/shifts/shifts.ts';
import { MeasurementDelay } from '../../../../shared/types/measurementDelay.ts';
import { ShiftSlot } from '../../../../shared/types/shiftTypes.ts';
import { Bar, BarGraphData, BarGroup, Label } from '../barGraphData.ts';
import { PeriodOption, ReportSettings } from '../report-settings/reportSettings.ts';
import { getShiftSlotColor } from '../reportGraphColors.ts';


interface MeasurementDelay2 extends MeasurementDelay {
    isOnWeekend: boolean;
}

export function prepareMeasurementCounts(
    measurementDelays: MeasurementDelay[],
    reportSettings: ReportSettings,
): BarGraphData {
    const periods = reportSettings.periodsInRange;
    const splitShifts = reportSettings.shiftsDisplay === 'Separated';
    const resolutionIsWeekly = reportSettings.resolution === 'Weeks';

    const transformed = transformInputs(measurementDelays);
    const filtered = filterInitial(transformed, reportSettings);

    const barGroups: BarGroup[] = [];
    for (const period of periods) {
        const barGroup = getBarGroup(
            filtered,
            period,
            splitShifts,
            resolutionIsWeekly,
            reportSettings.visibleShifts,
        );
        barGroups.push(barGroup);
    }

    const counts = barGroups.flatMap(barGroup => barGroup.bars.flatMap(bar => bar.sections.flatMap(section => section.value)));
    const maxValue = Math.max(...counts) ?? 0;

    return {
        maxValue,
        barGroups,
    }
}

function transformInputs(measurementDelays: MeasurementDelay[]): MeasurementDelay2[] {
    return measurementDelays.map(it => ({
        ...it,
        isOnWeekend: it.measurementShiftCode === 'W',
        measurementShiftCode: getShiftCodeAt(it.measurementTime!, false),//Override measurement shift code to convert W shifts to A/B/N
    }));
}

function filterInitial(
    measurementDelays: MeasurementDelay2[],
    reportSettings: ReportSettings,
): MeasurementDelay2[] {
    return measurementDelays
        .filter(it => reportSettings.visibleShifts.includes(it.measurementShiftSlot!))
        .filter(it => it.isOnWeekend && reportSettings.weekends || !it.isOnWeekend && reportSettings.workdays);
}

// A bar group correspons to one unit of time. It is either one bar or separated A/B shift bars
function getBarGroup(
    measurementDelays: MeasurementDelay[],
    period: PeriodOption,
    splitShifts: boolean,
    resolutionIsWeekly: boolean,
    visibleShifts: string[],
): BarGroup {
    const delaysInPeriod = measurementDelays
        .filter((it) => it.targetType !== 'during-setup')
        .filter((it) => it.measurementTime >= period.start && it.measurementTime < period.end);

    const bars = [];
    if (splitShifts) {
        const groupedDelays = splitABShifts(delaysInPeriod);
        const firstShiftA = isFirstShiftA(period);
        const aShiftBar = getBar(groupedDelays[0], 'A', resolutionIsWeekly ? (firstShiftA ? 'morning' : 'evening') : null);
        const bShiftBar = getBar(groupedDelays[1], 'B', resolutionIsWeekly ? (firstShiftA ? 'evening' : 'morning') : null);
        const nShiftBar = getBar(groupedDelays[2], 'N', resolutionIsWeekly ? 'night' : null);
        bars.push(firstShiftA ? aShiftBar : bShiftBar);
        bars.push(firstShiftA ? bShiftBar : aShiftBar);
        if (visibleShifts.includes('night')) {
            bars.push(nShiftBar);
        }
    } else {
        bars.push(getBar(delaysInPeriod, null, null));
    }

    return {
        periodLabel: period.periodLabel,
        bars,
    };
}

function isFirstShiftA(period: PeriodOption): boolean {
    const morningShiftTime = DateTime.fromJSDate(period.start).set({ hour: 10, minute: 0, second: 0, millisecond: 0 }); // Middle of morning shift
    const morningShiftCode = getShiftCodeAt(morningShiftTime);
    return morningShiftCode === 'A';
}

function splitABShifts(delays: MeasurementDelay[]): MeasurementDelay[][] {
    const delaysInShiftA = [];
    const delaysInShiftB = [];
    const delaysInShiftN = [];
    for (const measurementDelay of delays) {
        const shiftName = measurementDelay.measurementShiftCode;

        if (shiftName === 'A') {
            delaysInShiftA.push(measurementDelay);
        } else if (shiftName === 'B') {
            delaysInShiftB.push(measurementDelay);
        } else if (shiftName === 'N') {
            delaysInShiftN.push(measurementDelay);
        }
    }
    return [delaysInShiftA, delaysInShiftB, delaysInShiftN];
}

function getBar(delays: MeasurementDelay[], shiftLabel: string | null, shiftSlot: ShiftSlot | null): Bar {
    const measurementCount = delays.length;

    const barLabels: Label[] = shiftLabel
        ? [{ position: 'above-bottom', text: shiftLabel }]
        : [];

    return {
        sections: [{
            value: measurementCount,
            color: getShiftSlotColor(shiftSlot),
            labels: [{ position: 'below-top', text: measurementCount.toString() }],
        }],
        labels: barLabels,
    }
}
